import React, { useCallback, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { Button, Modal } from 'antd';
import {
    CameraOutlined,
    RotateRightOutlined,
    StopOutlined,
    SyncOutlined,
    VideoCameraOutlined
} from '@ant-design/icons';
import CameraInstructionsModal from './CameraInstructionsModal';

const EnhancedCameraModal = ({
    visible,
    onClose,
    onCapture,
    showInstructions = true,
    mode = 'survey' // 'survey' or 'fileUpload'
}) => {
    const webcamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const [mirrored, setMirrored] = useState(true);
    const [facingMode, setFacingMode] = useState('environment');
    const [imgSrc, setImgSrc] = useState(null);
    const [captureMode, setCaptureMode] = useState('photo'); // 'photo' or 'video'
    const [isRecording, setIsRecording] = useState(false);
    const [recordedChunks, setRecordedChunks] = useState([]);
    const [videoBlob, setVideoBlob] = useState(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const [timerInterval, setTimerInterval] = useState(null);
    const [showInstructionsModal, setShowInstructionsModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // 'photo' or 'video'

    const videoConstraints = {
        width: { min: 1280, ideal: 1920, max: 3840 },
        height: { min: 720, ideal: 1080, max: 2160 },
        facingMode: facingMode,
        frameRate: { ideal: 30, max: 60 },
        resizeMode: 'none',
        forceScreenshotSourceSize: true,
    };

    const capturePhoto = useCallback(() => {
        if (webcamRef.current) {
            const options = {
                width: webcamRef.current.video.videoWidth,
                height: webcamRef.current.video.videoHeight,
            };
            const imageSrc = webcamRef.current.getScreenshot(options);
            setImgSrc(imageSrc);
        }
    }, [webcamRef]);

    const handleDataAvailable = useCallback(({ data }) => {
        if (data.size > 0) {
            setRecordedChunks((prev) => [...prev, data]);
        }
    }, []);

    const startRecording = useCallback(() => {
        if (webcamRef.current && webcamRef.current.stream) {
            setRecordedChunks([]);
            const stream = webcamRef.current.stream;
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'video/webm'
            });
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.addEventListener('dataavailable', handleDataAvailable);
            mediaRecorder.addEventListener('stop', () => {
                // This will be handled in stopRecording after recordedChunks is updated
            });

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);

            // Start timer
            const interval = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
            setTimerInterval(interval);
        }
    }, [webcamRef, handleDataAvailable]);

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerInterval) {
                clearInterval(timerInterval);
                setTimerInterval(null);
            }
            // The blob will be created after recordedChunks is updated
            setTimeout(() => {
                setRecordedChunks(chunks => {
                    if (chunks.length > 0) {
                        const blob = new Blob(chunks, { type: 'video/webm' });
                        setVideoBlob(blob);
                    }
                    return chunks;
                });
            }, 100);
        }
    }, [isRecording, timerInterval]);

    // Create video preview URL
    const videoPreviewUrl = React.useMemo(() => {
        if (videoBlob) {
            return URL.createObjectURL(videoBlob);
        } else if (recordedChunks.length > 0) {
            const blob = new Blob(recordedChunks, { type: 'video/webm' });
            return URL.createObjectURL(blob);
        }
        return '';
    }, [videoBlob, recordedChunks]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleCaptureAction = (actionType) => {
        if (showInstructions) {
            setPendingAction(actionType);
            setShowInstructionsModal(true);
        } else {
            if (actionType === 'photo') {
                capturePhoto();
                setCaptureMode('photo');
            } else {
                startRecording();
                setCaptureMode('video');
            }
        }
    };

    const handleInstructionsContinue = () => {
        setShowInstructionsModal(false);
        if (pendingAction === 'photo') {
            capturePhoto();
            setCaptureMode('photo');
        } else if (pendingAction === 'video') {
            startRecording();
            setCaptureMode('video');
        }
        setPendingAction(null);
    };

    const handleInstructionsClose = () => {
        setShowInstructionsModal(false);
        setPendingAction(null);
    };

    const handleSave = async () => {
        if (captureMode === 'photo' && imgSrc) {
            // Convert base64 to File
            const response = await fetch(imgSrc);
            const blob = await response.blob();
            const fileName = `${mode}_photo_${Date.now()}.jpg`;
            const file = new File([blob], fileName, { type: 'image/jpeg' });
            onCapture(file);
            setImgSrc(null);
            onClose();
        } else if (captureMode === 'video' && (videoBlob || recordedChunks.length > 0)) {
            let blob = videoBlob;
            if (!blob && recordedChunks.length > 0) {
                blob = new Blob(recordedChunks, { type: 'video/webm' });
            }
            const fileName = `${mode}_video_${Date.now()}.webm`;
            const file = new File([blob], fileName, { type: 'video/webm' });
            onCapture(file);
            setVideoBlob(null);
            setRecordedChunks([]);
            onClose();
        }
    };

    const handleRetake = () => {
        if (captureMode === 'photo') {
            setImgSrc(null);
        } else {
            setVideoBlob(null);
            setRecordedChunks([]);
            setRecordingTime(0);
        }
    };

    const toggleMirroring = useCallback(() => {
        setMirrored(prev => !prev);
    }, []);

    const flipCamera = useCallback(() => {
        setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    }, []);

    const handleClose = () => {
        // Reset all states when closing
        setImgSrc(null);
        setVideoBlob(null);
        setRecordedChunks([]);
        setIsRecording(false);
        setRecordingTime(0);
        if (timerInterval) {
            clearInterval(timerInterval);
            setTimerInterval(null);
        }
        onClose();
    };

    // Show preview if we have captured content
    const hasCapture = imgSrc || videoBlob || recordedChunks.length > 0;

    return (
        <>
            <Modal
                title="Camera"
                open={visible}
                footer={null}
                onCancel={handleClose}
                width={1060}
                destroyOnClose={true}
                style={{ top: 20 }}
            >
                <div style={{
                    height: '70vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    {!hasCapture ? (
                        // Camera view
                        <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative'
                        }}>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative'
                            }}>
                                <Webcam
                                    audio={false}
                                    ref={webcamRef}
                                    screenshotFormat="image /jpeg"
                                    screenshotQuality={1}
                                    videoConstraints={videoConstraints}
                                    mirrored={mirrored}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain'
                                    }}
                                />

                                {isRecording && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 10,
                                        left: 10,
                                        background: 'red',
                                        color: 'white',
                                        padding: '5px 10px',
                                        borderRadius: 5,
                                        fontWeight: 'bold'
                                    }}>
                                        REC {formatTime(recordingTime)}
                                    </div>
                                )}
                            </div >

                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 10,
                                padding: '20px 0',
                                flexWrap: 'wrap'
                            }}>
                                {!isRecording ? (
                                    <>
                                        <Button
                                            type="primary"
                                            icon={<CameraOutlined />}
                                            onClick={() => handleCaptureAction('photo')}
                                            size="large"
                                        >
                                            Take Photo
                                        </Button>
                                        <Button
                                            icon={<VideoCameraOutlined />}
                                            onClick={() => handleCaptureAction('video')}
                                            size="large"
                                        >
                                            Record Video
                                        </Button>
                                        <Button
                                            icon={<SyncOutlined />}
                                            onClick={flipCamera}
                                            title="Flip Camera"
                                        />
                                        <Button
                                            icon={<RotateRightOutlined />}
                                            onClick={toggleMirroring}
                                            title="Toggle Mirror"
                                        />
                                    </>
                                ) : (
                                    <Button
                                        danger
                                        type="primary"
                                        icon={<StopOutlined />}
                                        onClick={stopRecording}
                                        size="large"
                                    >
                                        Stop Recording
                                    </Button>
                                )}
                            </div>
                        </div >
                    ) : (
                        // Preview view
                        <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                marginBottom: 20
                            }}>
                                {captureMode === 'photo' && imgSrc ? (
                                    <img
                                        src={imgSrc}
                                        alt="captured"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                ) : (
                                    <video
                                        src={videoPreviewUrl}
                                        controls
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                )}
                            </div>

                            <div style={{
                                display: 'flex',
                                gap: 10,
                                justifyContent: 'center',
                                padding: '10px 0'
                            }}>
                                <Button onClick={handleRetake}>
                                    Retake
                                </Button>
                                <Button type="primary" onClick={handleSave}>
                                    Save
                                </Button>
                            </div>
                        </div>
                    )}
                </div >
            </Modal >

            <CameraInstructionsModal
                visible={showInstructionsModal}
                onClose={handleInstructionsClose}
                onContinue={handleInstructionsContinue}
                isVideo={pendingAction === 'video'}
                mode={mode}
            />
        </>
    );
};

export default EnhancedCameraModal;
